/**
 * Active-guild port — host-owned owner of the per-app "which Discord guild
 * is selected" state plus the list of guilds the signed-in session can
 * edit. Moved from `features/gvg-planner/stores/session.ts` in Phase 2
 * so the feature no longer owns what every other surface (calendar,
 * officers, profile, onboarding) reads.
 *
 * After Phase 2:
 *
 * - The `activeDiscordGuildId` ref (localStorage-backed) + the
 *   `availableGuilds` / `dbGuildId` / `guildsLoading` /
 *   `ownerDiscordIdByGuildId` refs live here as module-level singletons
 *   (same lifetime as today's `composables/useLocalStorage` value).
 * - `setActive(next)` updates the ref + the cascade-event registry. The
 *   feature-scoped board/raid fetch work is **not** wired in — that
 *   moves to a cascade handler the planner store registers via
 *   `onActiveGuildChange()` (Phase 7). For Phase 2 the planner store
 *   keeps doing the cascade inline inside its `setActiveDiscordGuildId`
 *   action.
 * - `provideGuild(app)` exposes a thin adapter over the singletons via
 *   the `GUILD_PORT` injection key.
 * - `useGuildOrFallback()` is a host-only escape hatch for tests; the
 *   feature store uses `@/shared/ports/guild`'s `useGuild()` directly.
 */
import { inject, ref, watch, type App, type Ref } from 'vue'
import { gql, type TypedDocumentNode } from '@apollo/client/core'

import { apolloClient } from '@/shared/api/apolloClient'
import type { AppLocale } from '@/shared/i18n'

import { session as sessionRef, sessionLoading, fetchSession } from '@/app/session'
import { localePort } from '@/app/locale'
import {
  GUILD_PORT,
  _setGuildPortFallback,
  type GuildPort,
  type GuildSummary,
  type LockedGuildSummary,
} from '@/shared/ports/guild'

/** Re-export the guild-domain shape so consumers can `import { … } from '@/app/guild'`. */
export type { GuildSummary, LockedGuildSummary } from '@/shared/ports/guild'

/**
 * Validate a persisted snowflake without converting it through `Number()`.
 * Discord snowflakes exceed JavaScript's safe-integer range, so a numeric
 * round-trip would reject valid persisted guild IDs or silently change them.
 */
function isValidGuildSnowflake(raw: unknown): raw is string {
  if (typeof raw !== 'string') return false
  return /^\d{17,20}$/.test(raw) && !raw.startsWith('0')
}

const DEFAULT_DISCORD_GUILD_ID = String(import.meta.env['VITE_GUILD_ID'] ?? '0')
const LS_KEY = 'wwm-gvg-selected-guild-v1'

/** Load a string ref from `localStorage`, validating it as a Discord snowflake.
 * Mirrors the helper from `composables/useLocalStorage` but inlined here so
 * the host module owns the storage key rather than reaching across the
 * shared/composables boundary (which would invert the dependency arrow). */
function loadGuildFromStorage(): string {
  try {
    const raw = window.localStorage.getItem(LS_KEY)
    if (isValidGuildSnowflake(raw)) return raw
  } catch {
    /* localStorage unavailable (private mode / SSR / unit test) — fall through */
  }
  return DEFAULT_DISCORD_GUILD_ID
}

/** Active Discord guild snowflake — persisted across reloads. */
export const activeDiscordGuildId: Ref<string> = ref<string>(loadGuildFromStorage())

/** DB-internal id of the active guild, resolved through the MyGuilds query.
 * `null` until the first `loadAvailableGuilds` lands. */
export const dbGuildId: Ref<string | null> = ref<string | null>(null)

/** Guilds the signed-in session can view / edit, hydrated by `loadAvailableGuilds`. */
export const availableGuilds: Ref<ReadonlyArray<GuildSummary>> = ref<ReadonlyArray<GuildSummary>>([])

/**
 * Discord guild memberships blocked by the configured access role.
 *
 * These entries deliberately never enter `availableGuilds`, which makes it
 * impossible for selector and route callers to activate protected data by
 * mistake. They exist only to offer the access-request path.
 */
export const lockedGuilds: Ref<ReadonlyArray<LockedGuildSummary>> = ref<ReadonlyArray<LockedGuildSummary>>([])

/** `true` while a `MyGuilds` query is in flight. */
export const guildsLoading: Ref<boolean> = ref<boolean>(false)

/** Discord-owner lookup for the active guild (used by `activeGuildOwnerDiscordId`). */
export const ownerDiscordIdByGuildId: Ref<ReadonlyMap<string, string>> = ref<ReadonlyMap<string, string>>(new Map<string, string>())

// ── Internal mutations on the readonly-flavored refs ─────────────────────────
// The refs above are exported as `Readonly<Ref<…>>` for the public port
// surface. Internally we cast back to the mutable ref so the host
// module can update them.
function asMutable<T>(ref: Readonly<Ref<T>>): Ref<T> {
  return ref as Ref<T>
}

const mutableActiveDiscordGuildId = asMutable(activeDiscordGuildId)
const mutableDbGuildId = asMutable(dbGuildId)
const mutableAvailableGuilds = asMutable(availableGuilds)
const mutableLockedGuilds = asMutable(lockedGuilds)
const mutableGuildsLoading = asMutable(guildsLoading)
const mutableOwnerDiscordIdByGuildId = asMutable(ownerDiscordIdByGuildId)

/** Persist the active guild id to localStorage. Best-effort. */
function persistActiveGuild(value: string): void {
  try {
    if (isValidGuildSnowflake(value)) {
      window.localStorage.setItem(LS_KEY, value)
    } else {
      window.localStorage.removeItem(LS_KEY)
    }
  } catch {
    /* localStorage unavailable — swallow */
  }
}

// ── Cascade-event registry (Phase 7 surface) ─────────────────────────────────
// The host port exposes `onActiveGuildChange(cb)` so feature stores
// register their `fetchBoard / loadRaids / fetchPlayerTags` cascade in
// install*() during Phase 7. For Phase 2 the planner store keeps its
// existing inline cascade; the registry stays empty but live so the
// Phase 7 migration doesn't have to rewrite host wiring.
type CascadeHandler = () => void | Promise<void>
const cascadeHandlers: Set<CascadeHandler> = new Set<CascadeHandler>()

/** Resolve the DB id for a given Discord guild id (or `null` if unknown). */
async function resolveDbGuildId(discordGuildId: string): Promise<string | null> {
  const match = availableGuilds.value.find((g) => g.discordGuildId === discordGuildId)
  return match?.id != null ? String(match.id) : null
}

function mapGuildLanguage(raw: string | null): AppLocale | null {
  if (raw === null) return null
  const normalized = raw.toLowerCase()
  return normalized === 'en' || normalized === 'ru' ? normalized : null
}

function syncActiveGuildLocale(guild: GuildSummary | undefined): void {
  localePort.setActiveGuildLocale(guild?.language ?? null)
}

/** Update the active Discord guild id, resolve its DB id, and fire the cascade. */
async function setActive(next: string): Promise<void> {
  const selected = availableGuilds.value.find((guild) => guild.discordGuildId === next)
  // `availableGuilds` contains only server-authorized `canView` guilds. Do
  // not let any caller bypass the route guard and activate a locked entry.
  if (selected === undefined) return
  mutableActiveDiscordGuildId.value = next
  mutableDbGuildId.value = selected.id.toString()
  persistActiveGuild(next)
  syncActiveGuildLocale(selected)
  for (const cb of cascadeHandlers) {
    try {
      await cb()
    } catch {
      /* swallow — same fire-and-forget semantics as today's wiring */
    }
  }
}

/** Register a cascade handler. Returns an unsubscribe fn. */
function onActiveGuildChange(cb: CascadeHandler): () => void {
  cascadeHandlers.add(cb)
  return () => {
    cascadeHandlers.delete(cb)
  }
}

// ── MyGuilds GraphQL operation ──────────────────────────────────────────────
// Host-owned: the planner store previously held the document under
// `features/gvg-planner/lib/handWrittenGraphql.ts`. Phase 2 moves the
// host-coupling into `app/guild.ts` so the feature store doesn't have
// to import a hand-written operation that belongs to the host.
// A `canView: false` row is the request-access projection, not a weakened
// GuildSummary. Keep its metadata explicitly null so it cannot accidentally
// become a capability or settings source before the role check passes.
interface MyGuildIdentity {
  readonly id: number
  readonly name: string
  readonly discordGuildId: string | null
  readonly accessRoleDiscordId: string | null
  readonly accessRequested: boolean
  readonly language: string | null
}

interface ViewableMyGuild extends MyGuildIdentity {
  readonly canView: true
  readonly ownerDiscordId: string | null
  readonly officerRoleDiscordId: string | null
  readonly isMember: boolean
  readonly isOfficer: boolean
  readonly canEditHistoricalRaids: boolean
  readonly raidPublishChannelId: string | null
  readonly raidPublishWindowDays: number
  readonly completedRaidMessageRetentionHours: number
  readonly language: string | null
}

interface LockedMyGuildResponse extends MyGuildIdentity {
  readonly canView: false
  readonly ownerDiscordId: null
  readonly officerRoleDiscordId: null
  readonly isMember: null
  readonly isOfficer: null
  readonly canEditHistoricalRaids: null
  readonly raidPublishChannelId: null
  readonly raidPublishWindowDays: null
  readonly completedRaidMessageRetentionHours: null
  readonly language: null
}

// Shape mirrors the planner's hand-written operation verbatim — when
// codegen produces a real document later, swap the cast for the
// generated `TypedDocumentNode`.
type MyGuildsQuery = {
  readonly myGuilds: ReadonlyArray<ViewableMyGuild | LockedMyGuildResponse>
}
type MyGuildsQueryVariables = Record<string, never>

const MyGuildsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MyGuilds' },
      variableDefinitions: [],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'myGuilds' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discordGuildId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'ownerDiscordId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'officerRoleDiscordId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accessRoleDiscordId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'canView' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accessRequested' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isMember' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isOfficer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'canEditHistoricalRaids' } },
                { kind: 'Field', name: { kind: 'Name', value: 'raidPublishChannelId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'raidPublishWindowDays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'completedRaidMessageRetentionHours' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<MyGuildsQuery, MyGuildsQueryVariables>

const RequestGuildAccessDocument = gql`
  mutation RequestGuildAccess($guildId: Int!) {
    requestGuildAccess(guildId: $guildId)
  }
`

/**
 * Hydrate `availableGuilds` from the MyGuilds query. The signed-in
 * session must exist for the resolver to return data — when there's no
 * session the list is cleared.
 *
 * Intentionally does **not** auto-switch guilds (Phase 7's cascade
 * handlers take that over). The legacy auto-switch behavior ("if the
 * persisted guild isn't in the list, switch to the first") lived here
 * before Phase 2; the planner store now runs that check itself (it
 * already needs the cascade work to fire on the switch). A completed
 * authorization refresh does deselect an active guild the server no longer
 * permits, so a stored guild id remains a preference rather than a grant.
 */
async function loadAvailableGuilds(): Promise<void> {
  if (!sessionRef.value) {
    mutableAvailableGuilds.value = []
    mutableLockedGuilds.value = []
    mutableOwnerDiscordIdByGuildId.value = new Map<string, string>()
    return
  }
  mutableGuildsLoading.value = true
  try {
    const res = await apolloClient.query<MyGuildsQuery>({
      query: MyGuildsDocument,
      fetchPolicy: 'network-only',
    })
    const owners = new Map<string, string>()
    const viewable: Array<GuildSummary> = []
    const locked: Array<LockedGuildSummary> = []
    for (const g of res.data?.myGuilds ?? []) {
      const discordGuildId = g.discordGuildId
      if (discordGuildId === null) continue
      if (g.canView) {
        const ownerStr = g.ownerDiscordId ?? '0'
        viewable.push({
          id: g.id,
          name: g.name,
          discordGuildId,
          ownerDiscordId: ownerStr,
          officerRoleDiscordId: g.officerRoleDiscordId,
          accessRoleDiscordId: g.accessRoleDiscordId,
          isOfficer: g.isOfficer,
          canEditHistoricalRaids: g.canEditHistoricalRaids,
          raidPublishChannelId: g.raidPublishChannelId,
          raidPublishWindowDays: g.raidPublishWindowDays,
          completedRaidMessageRetentionHours: g.completedRaidMessageRetentionHours,
          language: mapGuildLanguage(g.language),
        })
        if (ownerStr !== '0') {
          owners.set(String(g.id), ownerStr)
        }
      } else {
        locked.push({
          id: g.id,
          name: g.name,
          discordGuildId,
          accessRequested: g.accessRequested,
        })
      }
    }
    mutableAvailableGuilds.value = viewable
    mutableLockedGuilds.value = locked
    mutableOwnerDiscordIdByGuildId.value = owners
    const active = viewable.find(
      (guild) => guild.discordGuildId === mutableActiveDiscordGuildId.value,
    )
    if (active === undefined) {
      clearActiveGuild()
    } else {
      mutableDbGuildId.value = String(active.id)
      syncActiveGuildLocale(active)
    }
  } catch {
    // Never retain a stale viewable list after an authorization refresh fails.
    // A transient failure can hide a guild, but it must not leave a recently
    // role-revoked guild eligible for selector or route activation.
    mutableAvailableGuilds.value = []
    mutableLockedGuilds.value = []
    mutableOwnerDiscordIdByGuildId.value = new Map<string, string>()
    clearActiveGuild()
  } finally {
    mutableGuildsLoading.value = false
  }
}

/**
 * Clear a persisted selection that the server no longer authorizes. This is
 * intentionally not `setActive('0')`: `setActive` accepts only a verified
 * `availableGuilds` entry, while this is the host's fail-closed cleanup path.
 */
function clearActiveGuild(): void {
  mutableActiveDiscordGuildId.value = '0'
  mutableDbGuildId.value = null
  persistActiveGuild('0')
  syncActiveGuildLocale(undefined)
}

/**
 * Ask for the configured role on a known locked membership. The backend
 * checks both current Discord membership and the guild's access policy; this
 * client-side membership check simply prevents arbitrary IDs from becoming a
 * request surface.
 */
async function requestGuildAccess(discordGuildId: string): Promise<boolean> {
  const candidate = lockedGuilds.value.find((guild) => guild.discordGuildId === discordGuildId)
  if (candidate === undefined) return false

  const result = await apolloClient.mutate<{ readonly requestGuildAccess: boolean }>({
    mutation: RequestGuildAccessDocument,
    variables: { guildId: discordGuildId },
  })
  const requested = result.data?.requestGuildAccess ?? false
  if (requested) await loadAvailableGuilds()
  return requested
}

const guildPortImpl: GuildPort = {
  activeDiscordGuildId,
  dbGuildId,
  availableGuilds,
  lockedGuilds,
  loading: guildsLoading,
  ownerDiscordIdByGuildId,
  setActive,
  clearActive: clearActiveGuild,
  loadAvailableGuilds,
  requestGuildAccess,
  resolveDbGuildId,
  onActiveGuildChange,
}

/**
 * Wire the implementation into the Vue injection key AND register
 * the non-injection fallback so non-setup callers (`installGvgPlanner`,
 * `gvgPlannerNavLinks`, host test harnesses) reach the same instance
 * via `useGuild()` without throwing `GuildPort not provided`.
 */
export function provideGuild(app: App): void {
  app.provide(GUILD_PORT, guildPortImpl)
  _setGuildPortFallback(guildPortImpl)
}

/** Host-side fallback for tests; preferred path is `@/shared/ports/guild`. */
export function useGuildOrFallback(): GuildPort {
  const fromPort = inject(GUILD_PORT, null)
  if (fromPort) return fromPort
  return guildPortImpl
}

// ── Session→guild cross-port glue ────────────────────────────────────────────
// Re-fetch the available guilds whenever the signed-in session flips
// (login / logout). The planner store wires the same watcher today;
// hosting it here is a cleanup the cascade-handler migration made
// trivial.
watch(
  () => sessionRef.value?.discordUserId ?? null,
  (uid) => {
    if (uid !== null) void loadAvailableGuilds()
    else {
      mutableAvailableGuilds.value = []
      mutableLockedGuilds.value = []
      mutableOwnerDiscordIdByGuildId.value = new Map<string, string>()
    }
  },
  { immediate: true },
)

// Touch the session exports so a future refactor that drops them keeps
// the dependency explicit.
void sessionLoading
void fetchSession
